<template>
  <!-- If a href is provided, we render a link rather than a button. -->
  <a v-if="href" :href="href" class="btn" :id="id" :class="buttonClass" @click="emit('click', $event)">
    <n-icon :icon="icon" v-if="icon" class="btn__icon" :class="{ 'btn__icon--no-text': !hasSlotContent }"></n-icon>
    <slot></slot>
  </a>
  <!-- Otherwise use a button -->
  <button class="btn" v-else :id="id" :class="buttonClass" @click="emit('click', $event)" :disabled="disabled">
    <n-icon :icon="icon" v-if="icon" class="btn__icon" :class="{ 'btn__icon--no-text': !hasSlotContent }"></n-icon>
    <slot></slot>
  </button>
</template>

<script lang="ts" setup>
import { computed, useSlots, PropType } from 'vue';
import NIcon from '../../brand/icon/icon.vue';
import { IconType } from '@shared/designsystem/components/brand/icon/icon.vue';

defineOptions({
  name: 'n-button',
});

// Define props for the component.
const props = defineProps({
  variant: {
    type: String as PropType<
      | 'primary'
      | 'secondary'
      | 'split'
      | 'success'
      | 'danger'
      | 'ghost'
      | 'action'
      | 'primary--outline'
      | 'secondary--outline'
    >,
  },
  href: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
  },
  icon: {
    type: String as PropType<IconType>,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (e: 'click'): void;
}>();

// Computed property for button class
const buttonClass = computed(() => {
  return {
    'btn--primary': props.variant === 'primary' || props.variant === 'primary--outline',
    'btn--secondary': props.variant === 'secondary' || props.variant === 'secondary--outline',
    'btn--success': props.variant === 'success',
    'button is-info': props.variant === 'split',
    'btn--danger': props.variant === 'danger',
    'btn--action': props.variant === 'action',
    'btn--outline': props.variant === 'primary--outline' || props.variant === 'secondary--outline',
    'btn--ghost': props.variant === 'ghost',
    'btn--loading': props.loading,
    'btn--icon': props.icon,
    'btn--no-text': !hasSlotContent.value,
  };
});

// Computed property to check if the slot is empty
const hasSlotContent = computed(() => {
  return (
    slots.default && slots.default().some((vnode) => vnode.children && vnode.children.toString().trim().length > 0)
  );
});

// Access to the slots
const slots = useSlots();
</script>

<style lang="scss" scoped>
@use '../../../styles/colours' as *;
@use '../../../styles/animations' as *;

.btn {
  background-color: colourScheme('light');
  border: 1px solid colourScheme('grey', 'light');
  color: colourScheme('body-text');
  border-radius: 4px;
  font-family: inherit;
  font-size: 1rem;
  cursor: pointer;
  justify-content: center;
  display: inline-flex;
  padding: calc(0.375rem - 1px) 0.75rem;
  text-align: center;
  white-space: nowrap;
  transition:
    background 0.3s,
    border-color 0.3s;
  text-decoration: none;
  line-height: 1.5;
  align-items: center;
  &:hover,
  &:focus {
    outline: none;
    border-color: #b5b5b5;
    color: #363636;
  }
  &:focus:not(:active) {
    box-shadow: 0 0 0 0.125em rgba(colourScheme('info'), 0.25);
  }
  &:disabled {
    background-color: colourScheme('disabled');
    border-color: colourScheme('grey', 'light');
    color: colourScheme('grey');
    box-shadow: none;
    cursor: not-allowed;
    &:hover,
    &:focus {
      background-color: colourScheme('disabled', dark);
      border-color: colourScheme('grey', 'light');
      color: colourScheme('grey');
    }
  }
  &__icon {
    margin-right: 0.25rem;
    display: block;
    :deep(svg) {
      display: block;
    }
  }
  &.btn--icon {
    &.btn--no-text {
      padding: 0.5625em;
      &.btn--action {
        padding: 0.1em 0.15em;
      }
      .btn__icon {
        margin-right: 0;
      }
    }
  }
  &.btn--primary {
    background-color: colourScheme('deep-sea');
    border-color: transparent;
    color: colourScheme('light');
    &:hover,
    &:focus {
      background-color: colourScheme('deep-sea', 'dark');
      border-color: transparent;
      color: colourScheme('light');
    }
  }
  &.btn--secondary {
    background-color: colourScheme('jonquil');
    border-color: transparent;
    color: colourScheme('body-text');
    &:hover,
    &:focus {
      background-color: colourScheme('jonquil', 'light');
    }
  }
  &.btn--tertiary {
    border-color: transparent;
    background-color: colourScheme('salmon');
    color: colourScheme('light');
    &:hover,
    &:focus {
      background-color: colourScheme('salmon', 'dark');
      border-color: colourScheme('salmon', 'dark');
      color: colourScheme('light');
    }
  }
  &.btn--danger {
    background-color: colourScheme('danger');
    border-color: transparent;
    color: colourScheme('light');
    &:hover,
    &:focus {
      background-color: colourScheme('danger', 'dark');
      border-color: transparent;
      color: colourScheme('light');
    }
  }
  &.btn--success {
    background-color: colourScheme('success');
    border-color: transparent;
    color: colourScheme('light');
    &:hover,
    &:focus {
      background-color: colourScheme('success', 'dark');
      border-color: transparent;
      color: colourScheme('light');
    }
  }
  &.btn--action {
    border-color: transparent;
    color: #aaa;
    &:hover {
      color: #6d6d6d;
    }
    &.btn--no-text {
      padding: 0;
      .btn__icon {
        height: 1.5em;
        width: 1.5em;
      }
    }
  }
  &.btn--ghost {
    background: transparent;
    border-color: transparent;
    color: colourScheme('link');
    &:hover,
    &:focus {
      color: colourScheme('deep-sea');
    }
    &:disabled {
      color: colourScheme('body-text');
      opacity: 0.6;
      background: transparent;

      &:hover,
      &:focus {
        border-color: transparent;
      }
    }
  }
  &.btn--action {
    color: #c4c7c3;
    background: transparent;
    border: none;
    .n-icon {
      height: 1.5em;
      width: 1.5em;
    }
    &:hover {
      color: #6d6d6d;
      background: transparent;
      border: none;
    }
    &:disabled {
      color: #c4c7c3;
    }
  }
  &.btn--loading {
    color: transparent !important;
    pointer-events: none;
    position: relative;
    user-select: none;
    &::after {
      border: 2px solid #dbdbdb;
      border-radius: 290486px;
      border-right-color: transparent;
      border-top-color: transparent;
      content: '';
      display: block;
      height: 1em;
      width: 1em;
      animation: spinAround 500ms infinite linear;
      left: calc(50% - 0.5em);
      top: calc(50% - 0.5em);
      position: absolute !important;
    }
    &:hover,
    &:focus {
      color: transparent !important;
    }
  }
  &.btn--primary,
  &.btn--secondary,
  &.btn--tertiary,
  &.btn--danger,
  &.btn--success,
  &.btn--loading {
    &:disabled {
      opacity: 0.5;
    }
  }
  &.btn--outline {
    background: transparent;
    color: colourScheme('body-text');
    &.btn--primary {
      border-color: colourScheme('deep-sea');
      color: colourScheme('deep-sea');
      &:hover,
      &:focus {
        color: colourScheme('light');
      }
    }
    &.btn--secondary {
      border-color: colourScheme('jonquil');
      color: colourScheme('jonquil');
      &:hover,
      &:focus {
        border-color: transparent;
        color: colourScheme('body-text');
      }
    }
    &.btn--tertiary {
      border-color: colourScheme('salmon');
      color: colourScheme('salmon');
      &:hover,
      &:focus {
        border-color: transparent;
        color: colourScheme('light');
      }
    }
    &.btn--danger {
      border-color: colourScheme('danger');
      color: colourScheme('danger');
      &:hover,
      &:focus {
        border-color: transparent;
        color: colourScheme('light');
      }
    }
    &.btn--success {
      border-color: colourScheme('success');
      color: colourScheme('success');
      &:hover,
      &:focus {
        border-color: transparent;
        color: colourScheme('light');
      }
    }
    &:disabled {
      opacity: 0.5;
    }
  }
}
</style>
